import { StyleSheet } from '@react-pdf/renderer';
import { colors, fontFamily, page, radii, space, typeScale } from './designTokens';

export const pdf = StyleSheet.create({
    // ---------- Page chrome ----------
    page: {
        paddingTop: page.paddingTop,
        paddingBottom: page.paddingBottom,
        paddingLeft: page.paddingLeft,
        paddingRight: page.paddingRight,
        fontFamily: fontFamily.body,
        fontSize: typeScale.body,
        lineHeight: 1.5,
        color: colors.ink10,
        backgroundColor: colors.paper,
    },
    runningHeader: {
        position: 'absolute',
        top: page.headerOffset,
        left: page.paddingLeft,
        right: page.paddingRight,
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        fontSize: typeScale.caption,
        color: colors.ink40,
        letterSpacing: 0.7,
        textTransform: 'uppercase',
        fontFamily: fontFamily.body,
        paddingBottom: 3,
        gap: 12,
    },
    runningHeaderRule: {
        position: 'absolute',
        top: page.headerOffset + 14,
        left: page.paddingLeft,
        right: page.paddingRight,
        height: 0.5,
        backgroundColor: colors.rule,
    },
    runningHeaderHalf: {
        maxWidth: '48%',
        overflow: 'hidden',
    },
    runningFooter: {
        position: 'absolute',
        bottom: page.footerOffset,
        left: page.paddingLeft,
        right: page.paddingRight,
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        fontSize: typeScale.caption,
        color: colors.ink40,
        fontFamily: fontFamily.mono,
        paddingTop: 8,
        gap: 12,
    },
    runningFooterRule: {
        position: 'absolute',
        bottom: page.footerOffset + 18,
        left: page.paddingLeft,
        right: page.paddingRight,
        height: 0.5,
        backgroundColor: colors.rule,
    },
    brandStrip: { height: 0 }, // retained to keep call-sites safe; no longer rendered
    hairline: { height: 0.5, backgroundColor: colors.rule },

    // ---------- Cover ----------
    cover: {
        flex: 1,
        paddingTop: space.sp8,
        paddingBottom: space.sp8,
        paddingLeft: space.sp12,
        paddingRight: space.sp8,
        color: colors.paper,
    },
    coverKicker: {
        fontSize: typeScale.eyebrow,
        letterSpacing: 1,
        color: colors.ink80,
        textTransform: 'uppercase',
        fontFamily: fontFamily.body,
        fontWeight: 500,
    },
    coverAccentBar: {
        width: 120,
        height: 1,
        backgroundColor: colors.accent,
        marginTop: space.sp6,
        marginBottom: space.sp4,
    },
    coverTitle: {
        fontSize: typeScale.displayH1,
        lineHeight: 1.05,
        letterSpacing: -0.8,
        fontFamily: fontFamily.display,
        color: colors.paper,
        fontWeight: 400,
    },
    coverSubtitle: {
        fontSize: typeScale.metricSm,
        fontFamily: fontFamily.mono,
        color: colors.ink80,
        marginTop: space.sp3,
        lineHeight: 1.45,
    },
    coverMetaRow: { marginTop: space.sp6, flexDirection: 'column', gap: space.sp2 },
    coverMetaItem: { flexDirection: 'row', gap: space.sp3, alignItems: 'baseline' },
    coverMetaLabel: {
        fontSize: typeScale.micro,
        color: colors.ink60,
        letterSpacing: 0.6,
        textTransform: 'uppercase',
        width: 72,
        fontFamily: fontFamily.body,
        fontWeight: 500,
    },
    coverMetaValue: {
        fontSize: typeScale.body,
        fontFamily: fontFamily.mono,
        color: colors.paper,
    },
    coverFooter: {
        marginTop: 'auto',
        flexDirection: 'row',
        justifyContent: 'flex-end',
        alignItems: 'flex-end',
    },
    coverFooterLabel: {
        fontSize: typeScale.micro,
        color: colors.ink60,
        letterSpacing: 1.6,
        textTransform: 'uppercase',
        fontFamily: fontFamily.body,
        fontWeight: 500,
    },
    coverFooterValue: {
        fontSize: typeScale.metricSm,
        fontFamily: fontFamily.mono,
        color: colors.paper,
        marginTop: 2,
    },

    // ---------- Section header (eyebrow + display H2) ----------
    section: { marginTop: space.sp6 },
    sectionHeader: { marginBottom: space.sp4 },
    eyebrow: {
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: space.sp2,
    },
    eyebrowDot: {
        width: 4,
        height: 4,
        backgroundColor: colors.accent,
        marginRight: space.sp2,
    },
    eyebrowText: {
        fontSize: typeScale.eyebrow,
        fontFamily: fontFamily.body,
        fontWeight: 600,
        color: colors.ink40,
        letterSpacing: 0.8,
        textTransform: 'uppercase',
    },
    sectionTitle: {
        fontSize: typeScale.displayH2,
        fontFamily: fontFamily.display,
        fontWeight: 400,
        color: colors.ink00,
        lineHeight: 1.1,
        letterSpacing: -0.25,
    },
    sectionSubtitle: {
        fontSize: typeScale.bodyLg,
        color: colors.ink20,
        marginTop: 3,
        lineHeight: 1.45,
        maxWidth: 440,
    },
    sectionIndex: {
        fontSize: typeScale.caption,
        color: colors.ink40,
        fontFamily: fontFamily.mono,
    },
    sectionDivider: { height: 0.5, backgroundColor: colors.rule, marginVertical: space.sp4 },

    // H4 (sub-section)
    h4: { fontSize: typeScale.h4, fontWeight: 600, color: colors.ink00, marginBottom: space.sp2, fontFamily: fontFamily.body },
    h4Mini: { fontSize: typeScale.h4Mini, fontWeight: 600, color: colors.ink00, marginBottom: space.sp1, fontFamily: fontFamily.body },

    paragraph: {
        fontSize: typeScale.body,
        color: colors.ink10,
        lineHeight: 1.55,
        marginBottom: space.sp2,
    },

    bullet: { flexDirection: 'row', marginBottom: 3 },
    bulletDot: { width: 10, color: colors.accent, fontFamily: fontFamily.body, fontWeight: 600 },
    bulletText: { flex: 1, fontSize: typeScale.body, color: colors.ink10, lineHeight: 1.5 },

    // ---------- Key/value ----------
    kvRow: {
        flexDirection: 'row',
        paddingVertical: 4,
        borderBottomWidth: 0.5,
        borderBottomColor: colors.ruleSoft,
    },
    kvLabel: {
        width: '38%',
        fontSize: typeScale.bodySm,
        color: colors.ink40,
        letterSpacing: 0.4,
        textTransform: 'uppercase',
        fontFamily: fontFamily.body,
        fontWeight: 500,
    },
    kvValue: { flex: 1, fontSize: typeScale.body, color: colors.ink10, fontFamily: fontFamily.mono },

    // ---------- KPI / stat cards ----------
    // minHeight tuned so a 2-line label still doesn't push value/sub into overlap:
    //   padding 8.5×2 = 17pt outer
    //   label (2 lines, lineHeight 1.3, fontSize 6.5) = ~17pt + marginBottom 6 = 23pt
    //   value (lineHeight 1.2, fontSize 13)            = ~16pt + marginBottom 6 = 22pt
    //   sub   (lineHeight 1.4, fontSize 6.5)           = ~9pt
    //   total content max                              = 54pt
    //   minHeight needed                               = 54 + 17 = 71pt → rounded up to 80pt
    kpiGrid: { flexDirection: 'row', gap: space.sp3, marginTop: space.sp2, flexWrap: 'wrap' },
    kpiCard: {
        flexGrow: 1,
        flexBasis: 110,
        padding: space.sp3,
        borderRadius: radii.md,
        borderWidth: 0.5,
        borderColor: colors.rule,
        backgroundColor: colors.paper,
        minHeight: 80,
    },
    kpiLabel: {
        fontSize: typeScale.micro,
        color: colors.ink40,
        letterSpacing: 0.7,
        textTransform: 'uppercase',
        marginBottom: 6,
        fontFamily: fontFamily.body,
        fontWeight: 600,
        lineHeight: 1.3,
    },
    kpiValue: {
        fontSize: typeScale.metricMd,
        fontFamily: fontFamily.mono,
        fontWeight: 500,
        color: colors.ink00,
        marginBottom: 6,
        lineHeight: 1.2,
    },
    kpiSub: { fontSize: typeScale.micro, color: colors.ink40, lineHeight: 1.4 },

    // ---------- Tables ----------
    table: {
        marginTop: space.sp2,
        borderTopWidth: 1.2,
        borderTopColor: colors.ink10,
        borderBottomWidth: 1.2,
        borderBottomColor: colors.ink10,
    },
    tableHeadRow: {
        flexDirection: 'row',
        paddingVertical: 6,
        paddingHorizontal: 0,
        borderBottomWidth: 1,
        borderBottomColor: colors.ink10,
    },
    tableHeadCell: {
        fontSize: typeScale.tableHead,
        color: colors.ink40,
        letterSpacing: 0.6,
        textTransform: 'uppercase',
        fontFamily: fontFamily.body,
        fontWeight: 600,
        overflow: 'hidden',
        lineHeight: 1.3,
    },
    tableRow: {
        flexDirection: 'row',
        // Bumped from 6 → 8pt — gives wrapped multi-line cells breathing
        // room top + bottom so adjacent rows aren't visually crowded. Row
        // height now grows naturally with the tallest cell.
        paddingVertical: 8,
        paddingHorizontal: 2,
        borderBottomWidth: 0.5,
        borderBottomColor: colors.ruleSoft,
        alignItems: 'flex-start',
        minHeight: 22,
    },
    tableRowAlt: { backgroundColor: colors.surface },
    tableRowSection: {
        backgroundColor: colors.surfaceAlt,
        paddingVertical: 4,
        paddingHorizontal: 4,
        borderBottomWidth: 0.5,
        borderBottomColor: colors.rule,
    },
    tableRowSectionText: {
        fontSize: typeScale.caption,
        letterSpacing: 0.8,
        textTransform: 'uppercase',
        color: colors.ink40,
        fontFamily: fontFamily.body,
        fontWeight: 600,
    },
    tableRowTotal: {
        flexDirection: 'row',
        paddingVertical: 5,
        paddingHorizontal: 4,
        borderTopWidth: 1,
        borderTopColor: colors.ink10,
        backgroundColor: colors.surface,
        gap: 6,
    },
    tableCell: {
        fontSize: typeScale.tableCell,
        color: colors.ink10,
        overflow: 'hidden',
        lineHeight: 1.4,
        fontFamily: fontFamily.body,
    },
    tableCellNum: { fontFamily: fontFamily.mono, textAlign: 'right' },

    // ---------- Badges ----------
    chip: {
        paddingHorizontal: 5,
        paddingTop: 1,
        paddingBottom: 3,
        borderRadius: radii.sm,
        fontSize: 7,
        lineHeight: 1,
        letterSpacing: 0.4,
        textTransform: 'uppercase',
        fontFamily: fontFamily.body,
        fontWeight: 600,
        alignSelf: 'flex-start',
    },

    divider: { height: 0.5, backgroundColor: colors.rule, marginVertical: space.sp3 },

    // ---------- Cards ----------
    cardGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: space.sp3 },
    card: {
        flexGrow: 1,
        flexBasis: '45%',
        padding: space.sp3,
        borderRadius: radii.md,
        borderWidth: 0.5,
        borderColor: colors.rule,
        backgroundColor: colors.paper,
    },
    cardFlat: {
        backgroundColor: colors.surface,
        borderColor: colors.surface,
    },
    cardAccent: {
        borderLeftWidth: 2,
        borderLeftColor: colors.accent,
    },
    cardWarn: {
        borderLeftWidth: 2,
        borderLeftColor: colors.warn,
    },
    cardTitle: { fontSize: typeScale.h4, fontWeight: 600, color: colors.ink00, marginBottom: 3, fontFamily: fontFamily.body },
    cardMeta: { fontSize: typeScale.caption, color: colors.ink40, marginBottom: 4, fontFamily: fontFamily.body },
    cardBody: { fontSize: typeScale.body, color: colors.ink10, lineHeight: 1.45 },

    // ---------- Month heatmap ----------
    monthGridWrap: { marginTop: space.sp2 },
    monthGridRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 2 },
    monthCell: {
        width: 32,
        height: 26,
        borderRadius: radii.sm,
        justifyContent: 'center',
        alignItems: 'center',
        padding: 2,
    },
    monthCellLabel: { fontSize: typeScale.micro, color: colors.paper, fontFamily: fontFamily.body, fontWeight: 600 },

    // ---------- Sparkbar (retained for compat) ----------
    sparkbar: { height: 40, flexDirection: 'row', alignItems: 'flex-end', gap: 2, marginTop: space.sp2 },
    sparkbarCol: { flex: 1, backgroundColor: colors.accent, borderRadius: 1 },
});

export default pdf;
